// The Inbox (SPEC 7.2): Needs an answer, Check at your desk, Finished (collapsed). An idle agent is
// never an alert. Every state in SPEC 7.3 that belongs here has a banner or an empty state. A card whose answer is on
// its way, or that isn't open for one yet, waits quietly in its own group: it neither needs an answer nor the desk.
import type { Agent, Card } from "@whistle/protocol";
import { useApp, useAppState } from "../app/AppContext";
import { onLinkClick } from "../app/router";
import { useNow } from "../app/useNow";
import { PRODUCT_NAME } from "../brand";
import { agentCounts, groupCards, machineStatus, readOnlyNote, requestText, showsTurnEnded, TURN_ENDED_TITLE, type AppState, type SendRecord } from "../lib/model";
import { displayText } from "../lib/sanitize";
import { ago, clock, duration, untilText } from "../lib/time";
import { Banner } from "../components/Banner";
import { IconChevron } from "../components/Icons";
import { ConnectionChip, Page, TabBar, TopBar } from "../components/Shell";
import { agentStripVisual, cardVisual, StateChip, stateIcon, STATE_WORD, type VisualState } from "../components/StateChip";

export const KIND_LABEL: Record<string, string> = { claude: "Claude Code", codex: "Codex", gemini: "Gemini CLI", opencode: "OpenCode", antigravity: "Antigravity", copilot: "Copilot CLI" };
export const kindLabel = (kind: string) => KIND_LABEL[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1);

export function Inbox() {
  const s = useAppState();
  const { controller } = useApp();
  const now = useNow();
  const groups = groupCards(s.cards, s.sends);
  const listed = groups.needs.length + groups.pending.length + groups.desk.length;
  const machine = s.machine?.name ?? s.pairing?.machine.name ?? "your PC";
  const ms = machineStatus(s);
  const stale = ms !== "online" || !s.online;
  const finishedOpen = s.prefs.finishedOpen;

  return (
    <>
      <TopBar right={<ConnectionChip />} />
      <Page>
        <StatusBanners s={s} now={now} />
        {s.notices.map((n) => (
          <Banner key={n.id} tone={noticeWarns(n.kind) ? "warn" : "info"} icon={noticeWarns(n.kind) ? "alert" : "info"} title={noticeTitle(n.kind)} onDismiss={() => controller.store.dispatch({ type: "dismissNotice", id: n.id })}>
            {n.text} <span className="faint">· {clock(n.at)}</span>
          </Banner>
        ))}

        {groups.needs.length > 0 && (
          <section className="group" aria-labelledby="g-needs">
            <div className="group-head">
              <h2 id="g-needs" className="label waiting">
                Needs an answer · {groups.needs.length}
              </h2>
            </div>
            <ul className="rows">
              {groups.needs.map((c) => (
                <Row key={c.requestId} card={c} sends={s.sends} now={now} stale={stale} />
              ))}
            </ul>
          </section>
        )}

        {groups.pending.length > 0 && (
          <section className="group" aria-labelledby="g-pending">
            <div className="group-head">
              <h2 id="g-pending" className="label">
                Waiting on {machine} · {groups.pending.length}
              </h2>
            </div>
            <ul className="rows">
              {groups.pending.map((c) => (
                <Row key={c.requestId} card={c} sends={s.sends} now={now} stale={stale} />
              ))}
            </ul>
          </section>
        )}

        {groups.desk.length > 0 && (
          <section className="group" aria-labelledby="g-desk">
            <div className="group-head">
              <h2 id="g-desk" className="label">
                Check at your desk · {groups.desk.length}
              </h2>
            </div>
            <ul className="rows">
              {groups.desk.map((c) => (
                <Row key={c.requestId} card={c} sends={s.sends} now={now} stale={stale} />
              ))}
            </ul>
          </section>
        )}

        {listed === 0 && !s.removed && <EmptyState s={s} machine={machine} />}

        {s.agents.length > 0 && listed > 0 && (
          <section className="group" aria-labelledby="g-agents">
            <div className="group-head">
              <h2 id="g-agents" className="label">
                All agents · {s.agents.length}
              </h2>
            </div>
            <AgentStrip agents={s.agents} cards={s.cards} sends={s.sends} />
          </section>
        )}

        {groups.finished.length > 0 && (
          <section className="group" aria-labelledby="g-fin">
            <div className="group-head">
              <h2 id="g-fin" className="label">
                Finished · {groups.finished.length}
              </h2>
              <button type="button" aria-expanded={finishedOpen} aria-controls="finished-list" onClick={() => controller.setPrefs({ finishedOpen: !finishedOpen })}>
                {finishedOpen ? "Hide" : "Show"} <IconChevron />
              </button>
            </div>
            {finishedOpen && (
              <ul className="rows" id="finished-list">
                {groups.finished.map((c) => (
                  <Row key={c.requestId} card={c} sends={s.sends} now={now} stale={stale} dim />
                ))}
              </ul>
            )}
          </section>
        )}

        {!s.removed && s.pairing && (
          <p className="small muted">
            <a href="/message" onClick={(e) => onLinkClick(e, "/message")}>
              Message an agent
            </a>{" "}
            · for idle and finished agents, when messages are on.
          </p>
        )}
      </Page>
      <TabBar />
    </>
  );
}

function noticeTitle(kind: string): string {
  switch (kind) {
    case "device-added":
      return "A phone was paired";
    case "device-removed":
      return "A phone was removed";
    case "setting-changed":
      return "A setting changed at the desk";
    case "update":
      return "Update available";
    default:
      return "Integration needs attention";
  }
}

/** Only an integration problem is a warning. Everything else, a new release included, is for information. */
function noticeWarns(kind: string): boolean {
  return kind === "integration";
}

export function StatusBanners({ s, now }: { s: AppState; now: number }) {
  const machine = s.machine?.name ?? s.pairing?.machine.name ?? "your PC";
  const ms = machineStatus(s);
  const out: JSX.Element[] = [];
  if (s.removed)
    out.push(
      <Banner key="removed" tone="danger" icon="lock" title="This phone was removed." action={{ label: "How to pair again", href: "/connections" }}>
        Pair again from your desk with <code>whistle pair</code>. Paying does not restore the right to answer.
      </Banner>,
    );
  else if (!s.online)
    out.push(
      <Banner key="offline" tone="warn" icon="cloud-off" title="You're offline.">
        Cards show their age. Answering is off until the phone is back online.
      </Banner>,
    );
  else if (s.conn === "connecting" || s.conn === "reconnecting")
    out.push(
      <Banner key="conn" icon="info" title={s.conn === "connecting" ? "Connecting to the relay." : "Reconnecting."}>
        Answering is off until the connection is back.
      </Banner>,
    );
  else if (ms === "unreachable")
    out.push(
      <Banner key="unreach" tone="warn" icon="cloud-off" title={`${machine} unreachable.`}>
        {s.machineLastSeen ? `Last seen ${clock(s.machineLastSeen)} (${ago(s.machineLastSeen, now)} ago).` : "Not seen since this phone connected."} Answering is off. Is {PRODUCT_NAME} running there?
      </Banner>,
    );
  if (s.settings?.remotePaused && !s.removed)
    out.push(
      <Banner key="paused" tone="warn" icon="lock" title={`Answers paused at ${machine}.`} action={{ label: "Resume in Connections", href: "/connections" }}>
        Cards are read-only until remote control is resumed.
      </Banner>,
    );
  if (s.settings?.alerts.mode === "quiet" && !s.removed)
    out.push(
      <Banner key="quiet" icon="bell-off" title={`Alerts quiet ${untilText(s.settings.alerts.until, now) || "until you change it"}.`} action={{ label: "Change in Connections", href: "/connections" }}>
        {machine} won't buzz this phone. The inbox still updates.
      </Banner>,
    );
  if (s.push.supported && (s.push.permission !== "granted" || !s.push.subscribed) && s.pairing && !s.removed)
    out.push(
      <Banner key="alerts-off" tone="warn" icon="bell-off" title="Alerts off on this phone." action={{ label: "Fix in Connections", href: "/connections" }}>
        You'll only see new questions while the app is open.
      </Banner>,
    );
  return <>{out}</>;
}

const ROW_CLASS: Partial<Record<VisualState, string>> = { waiting: "is-waiting", finished: "is-finished", moved: "is-moved", sent: "is-sent", pending: "is-pending" };

function Row({ card, sends, now, stale, dim }: { card: Card; sends: Record<string, SendRecord>; now: number; stale: boolean; dim?: boolean }) {
  const href = `/card/${encodeURIComponent(card.requestId)}`;
  const v = cardVisual(card, sends);
  const cls = ROW_CLASS[v] ?? "is-desk";
  const request = requestText(card);
  const what = card.kind === "question" ? card.title : request ? firstLine(displayText(request)) : card.title;
  // The chip says it all for an answer on its way; the "not taking an answer" note would only contradict it.
  const note = v === "sent" ? null : (readOnlyNote(card) ?? (showsTurnEnded(card, sends) ? TURN_ENDED_TITLE : null));
  const word = STATE_WORD[v];
  const status = note ? `${word}. ${note}` : `${word}: ${card.title}`;
  return (
    <li>
      <a className={`row ${cls}${dim ? " is-dim" : ""}`} href={href} onClick={(e) => onLinkClick(e, href)} aria-label={`${card.agentName}, ${kindLabel(card.agentKind)}, ${status}`}>
        <span className="bar" aria-hidden="true" />
        <span className="main">
          <span className="name">
            <b>{card.agentName}</b>
            <span className="kind">{kindLabel(card.agentKind)}</span>
          </span>
          <span className="what">{card.kind === "approval" && request ? <code>{what}</code> : what}</span>
          {note && <span className="note">{displayText(note)}</span>}
          <span className="ctx">
            {card.project ? `${card.project} · ` : ""}
            {card.envLabel}
          </span>
        </span>
        <span className="side">
          <span className="num" title={stale ? "Age at last update" : undefined}>
            {stale ? `${duration(now - card.createdAt)} old` : duration(now - card.createdAt)}
          </span>
          <StateChip state={v} />
        </span>
      </a>
    </li>
  );
}

function firstLine(text: string): string {
  const line = text.split("\n").find((l) => l.trim()) ?? "";
  return line.length > 90 ? `${line.slice(0, 88)}…` : line;
}

function EmptyState({ s, machine }: { s: AppState; machine: string }) {
  const ms = machineStatus(s);
  const envs = s.machine?.envs.map((e) => e.label).join(", ");
  if (ms !== "online" || s.conn !== "connected") {
    return (
      <div className="empty">
        <h2>Nothing to show yet.</h2>
        <p>{s.pairing ? `Waiting to hear from ${machine}.` : "Pair a phone from your desk to begin."}</p>
      </div>
    );
  }
  if (s.agents.length === 0) {
    return (
      <div className="empty">
        <h2>No agents running in herdr.</h2>
        <p>
          Connected to {machine}
          {envs ? ` · ${envs}` : ""}. Start an agent inside herdr and it appears here.
        </p>
        <p className="small">
          Agents already running outside herdr can't be answered until they're resumed inside it. <code>whistle doctor</code> at the desk explains how.
        </p>
      </div>
    );
  }
  const c = agentCounts(s.agents);
  return (
    <div className="empty">
      <h2>Nothing needs an answer.</h2>
      <p>
        {s.agents.length} agent{s.agents.length === 1 ? "" : "s"} on {machine}
        {c.working ? ` · ${c.working} working` : ""}
        {c.idle ? ` · ${c.idle} idle` : ""}
        {c.done ? ` · ${c.done} finished` : ""}.
      </p>
      <AgentStrip agents={s.agents} cards={s.cards} sends={s.sends} />
    </div>
  );
}

export function AgentStrip({ agents, cards, sends }: { agents: Agent[]; cards: Card[]; sends: Record<string, SendRecord> }) {
  const order: Agent["status"][] = ["blocked", "working", "unknown", "idle", "done"];
  const sorted = [...agents].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
  return (
    <ul className="agents" aria-label="Agents">
      {sorted.map((a) => {
        const v = agentStripVisual(a, cards, sends);
        return (
          <li key={a.id} className={`agent-pill is-${v}`}>
            {stateIcon(v)}
            <span className="n">{a.name}</span>
            <span className="s">{STATE_WORD[v]}</span>
          </li>
        );
      })}
    </ul>
  );
}
